import { useQuery } from "@tanstack/react-query";
import { getAuthHeaders } from "@/lib/queryClient";
import { Info, Building2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WaleTenancyBreakdown {
  id: string;
  tenantName: string;
  leaseStartDate: string;
  leaseEndDate: string;
  remainingYears: number;
  areaSquareMetres: number;
  annualRent: string;
  waleContribution: number;
  percentOfTotal: number;
}

interface WaleData {
  securityId: string;
  calculatedAt: string;
  waleByIncome: number;
  waleByArea: number;
  waleSimple: number;
  waleYears: number;
  waleMonths: number;
  occupancyRatePercent: number;
  totalRentAnnual: string;
  totalAreaSqm: string;
  tenancyCount: number;
  vacantTenancies: number;
  tenancies: WaleTenancyBreakdown[];
}

interface WaleDisplayCardProps {
  securityId: string;
  isActive?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CHART_COLOURS = [
  "hsl(220, 70%, 50%)",  // blue
  "hsl(160, 60%, 45%)",  // teal
  "hsl(280, 60%, 55%)",  // purple
  "hsl(30, 80%, 55%)",   // orange
  "hsl(340, 65%, 50%)",  // pink
  "hsl(190, 70%, 45%)",  // cyan
  "hsl(45, 80%, 50%)",   // gold
  "hsl(120, 50%, 45%)",  // green
];

function getRiskLevel(waleByArea: number): {
  label: string;
  colour: string;
  dotColour: string;
  badgeClass: string;
} {
  if (waleByArea > 5) {
    return {
      label: "Strong",
      colour: "text-green-600",
      dotColour: "bg-green-500",
      badgeClass: "bg-green-100 text-green-800 border-green-200",
    };
  }
  if (waleByArea >= 3) {
    return {
      label: "Moderate",
      colour: "text-amber-600",
      dotColour: "bg-amber-500",
      badgeClass: "bg-amber-100 text-amber-800 border-amber-200",
    };
  }
  return {
    label: "Weak",
    colour: "text-red-600",
    dotColour: "bg-red-500",
    badgeClass: "bg-red-100 text-red-800 border-red-200",
  };
}

function formatCurrency(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "$0";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function WaleLoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-6 w-24" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function WaleEmptyState() {
  return (
    <Card data-testid="card-wale-summary">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          WALE Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center py-8 text-center">
        <Building2 className="h-10 w-10 text-muted-foreground mb-3" />
        <p
          data-testid="value-wale-years"
          className="text-2xl font-bold text-muted-foreground"
        >
          N/A
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          Add tenancy data to calculate WALE
        </p>
        <div data-testid="indicator-wale-risk" className="mt-3 flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-gray-300" />
          <span className="text-xs text-muted-foreground">No data</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function WaleDisplayCard({
  securityId,
  isActive = true,
}: WaleDisplayCardProps) {
  const queryKey = [`/api/crm/securities/${securityId}/wale`];

  const {
    data: waleData,
    isLoading,
    error,
  } = useQuery<WaleData | null>({
    queryKey,
    queryFn: async () => {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`/api/crm/securities/${securityId}/wale`, {
        method: "GET",
        headers: authHeaders,
        credentials: "include",
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to load WALE data");
      }
      return response.json();
    },
    enabled: !!securityId && isActive,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  if (isLoading) {
    return <WaleLoadingSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8 text-center text-muted-foreground">
          <p>Error loading WALE data: {(error as Error).message}</p>
        </CardContent>
      </Card>
    );
  }

  // No data / no tenancies
  if (!waleData || waleData.tenancyCount === 0) {
    return <WaleEmptyState />;
  }

  const risk = getRiskLevel(waleData.waleByArea);
  const tenancies = waleData.tenancies || [];

  // Compute totals for footer row
  const totalArea = tenancies.reduce((s, t) => s + t.areaSquareMetres, 0);
  const totalRent = tenancies.reduce(
    (s, t) => s + parseFloat(t.annualRent || "0"),
    0
  );
  const totalContribution = tenancies.reduce(
    (s, t) => s + t.waleContribution,
    0
  );
  const totalPct = tenancies.reduce((s, t) => s + t.percentOfTotal, 0);

  return (
    <div className="space-y-6">
      {/* WALE Summary Card */}
      <Card data-testid="card-wale-summary">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>WALE Summary</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 rounded-full"
                  data-testid="tooltip-wale-methodology"
                  aria-label="WALE methodology info"
                >
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-80"
                data-testid="tooltip-wale-methodology-content"
              >
                <div className="space-y-2">
                  <p className="font-semibold">
                    Weighted Average Lease Expiry (WALE)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Formula: &Sigma;(Remaining Lease &times; Tenant Area) / Total
                    Area
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Calculation uses area-weighted method. Only active tenancies
                    are included. Expired leases and vacant areas are excluded.
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="text-sm font-medium text-muted-foreground">
              WALE:
            </span>
            <span
              data-testid="value-wale-years"
              className="text-3xl font-bold tracking-tight"
            >
              {waleData.waleByArea.toFixed(1)}
            </span>
            <span className="text-lg text-muted-foreground">years</span>
          </div>

          <p
            data-testid="value-wale-months"
            className="text-sm text-muted-foreground mb-4"
          >
            {waleData.waleYears} year{waleData.waleYears !== 1 ? "s" : ""}
            {waleData.waleMonths > 0
              ? `, ${waleData.waleMonths} month${waleData.waleMonths !== 1 ? "s" : ""}`
              : ""}
          </p>

          <div
            data-testid="indicator-wale-risk"
            className="flex items-center gap-2"
          >
            <span
              className={`inline-block h-3 w-3 rounded-full ${risk.dotColour}`}
            />
            <Badge className={risk.badgeClass} variant="outline">
              {risk.label}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div data-testid="table-wale-breakdown">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Lease End</TableHead>
                  <TableHead className="text-right">Remaining (yrs)</TableHead>
                  <TableHead className="text-right">Area (sqm)</TableHead>
                  <TableHead className="text-right">Annual Rent</TableHead>
                  <TableHead className="text-right">WALE Contribution</TableHead>
                  <TableHead className="text-right">% of Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenancies.map((t) => (
                  <TableRow
                    key={t.id}
                    data-testid={`row-tenant-${t.id}`}
                  >
                    <TableCell className="font-medium">
                      {t.tenantName}
                    </TableCell>
                    <TableCell>{formatDate(t.leaseEndDate)}</TableCell>
                    <TableCell className="text-right">
                      {t.remainingYears.toFixed(1)}
                    </TableCell>
                    <TableCell className="text-right">
                      {t.areaSquareMetres.toLocaleString("en-AU")}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(t.annualRent)}
                    </TableCell>
                    <TableCell className="text-right">
                      {t.waleContribution.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      {t.percentOfTotal.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-bold">Total</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-right font-bold">
                    {totalArea.toLocaleString("en-AU")}
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {formatCurrency(totalRent)}
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {totalContribution.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {totalPct.toFixed(1)}%
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Stacked Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>WALE Contribution by Tenant</CardTitle>
        </CardHeader>
        <CardContent>
          <div data-testid="chart-wale-breakdown" className="h-16">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  {
                    name: "WALE",
                    ...Object.fromEntries(
                      tenancies.map((t) => [t.tenantName, t.waleContribution])
                    ),
                  },
                ]}
                layout="vertical"
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              >
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value.toFixed(2)} yrs`,
                    name,
                  ]}
                />
                {tenancies.map((t, idx) => (
                  <Bar
                    key={t.id}
                    dataKey={t.tenantName}
                    stackId="wale"
                    fill={CHART_COLOURS[idx % CHART_COLOURS.length]}
                    radius={
                      idx === 0
                        ? [4, 0, 0, 4]
                        : idx === tenancies.length - 1
                          ? [0, 4, 4, 0]
                          : [0, 0, 0, 0]
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4">
            {tenancies.map((t, idx) => (
              <div key={t.id} className="flex items-center gap-1.5 text-xs">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{
                    backgroundColor:
                      CHART_COLOURS[idx % CHART_COLOURS.length],
                  }}
                />
                <span className="text-muted-foreground">{t.tenantName}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
